import imageRvuBackgroundLarge from 'assets/rvu/rvu-background-large.png';
import imageRvuBackgroundPlaceholder from 'assets/rvu/rvu-background-placeholder.png';
import imageRvuBackground from 'assets/rvu/rvu-background.png';
import imageRvuDashboardLarge from 'assets/rvu/rvu-dashboard-large.png';
import imageRvuDashboardPlaceholder from 'assets/rvu/rvu-dashboard-placeholder.png';
import imageRvuDashboard from 'assets/rvu/rvu-dashboard.png';
import imageRvuTrendsLarge from 'assets/rvu/rvu-trends-large.png';
import imageRvuTrendsPlaceholder from 'assets/rvu/rvu-trends-placeholder.png';
import imageRvuTrends from 'assets/rvu/rvu-trends.png';
import { Button } from 'components/Button';
import { CodeBlock } from 'components/CodeBlock';
import { Footer } from 'components/Footer';
import { Image } from 'components/Image';
import { Link } from 'components/Link';
import { Meta } from 'components/Meta';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'components/Table';
import { useTheme } from 'components/ThemeProvider';
import {
  ProjectBackground,
  ProjectContainer,
  ProjectHeader,
  ProjectImage,
  ProjectSection,
  ProjectSectionColumns,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'layouts/Project';
import { Fragment } from 'react';
import { media } from 'utils/style';
import styles from './rvu.module.css';

const title = 'rvu';
const description =
  'rvu lets you browse and analyse past Claude Code sessions, then relaunch one ' +
  'to pick up where you left off.';
const roles = ['Solo Project', 'TypeScript', 'Terminal UI', 'Published on npm'];

const install = `$ npx @txmxthy/rvu

  rvu · last 7 days · 40 sessions · 100% cache hit

  ▸ portfolio        12 sessions   2h ago
  ▸ rvu               9 sessions   yesterday
  ▸ space             6 sessions   3 days ago

  j/k move · enter resume · v preview · t trends · ? help`;

const launchers = [
  ['inline', 'Runs claude --resume in your terminal, then exits rvu.'],
  ['tmux', 'Opens the session in a new tmux window and stays running.'],
  ['workmux', 'Reconnects to the branch worktree, or creates one.'],
  ['workmux sandbox', 'Reconnects to the branch worktree, or creates one, inside a deny-by-default Docker container.'],
];

export function Rvu() {
  const { themeId } = useTheme();
  const isDark = themeId === 'dark';

  return (
    <Fragment>
      <Meta title={title} prefix="Projects" description={description} />
      <ProjectContainer>
        <ProjectBackground
          opacity={isDark ? 0.8 : 0.9}
          srcSet={[imageRvuBackground, imageRvuBackgroundLarge]}
          placeholder={imageRvuBackgroundPlaceholder}
        />
        <ProjectHeader
          title={title}
          description={description}
          url="https://github.com/txmxthy/rvu"
          linkLabel="View on GitHub"
          roles={roles}
        />
        <ProjectSection padding="top">
          <ProjectSectionContent>
            <ProjectImage
              srcSet={[imageRvuDashboard, imageRvuDashboardLarge]}
              placeholder={imageRvuDashboardPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 800px, 1000px`}
              alt="The rvu dashboard: a terminal list of Claude Code projects expanded into sessions, each row showing branch, cost and last activity, with a metrics preview pane alongside."
            />
            <ProjectTextRow>
              <ProjectSectionHeading>Sessions from every project</ProjectSectionHeading>
              <ProjectSectionText>
                Claude Code keeps every session on disk.{' '}
                <Link href="https://docs.claude.com/en/docs/claude-code">
                  claude --resume
                </Link>{' '}
                only shows sessions in the current directory. After a weekend away, I used
                to reconstruct my work from memory. rvu lists every session across every
                project on the machine.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection light={isDark}>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>One command, no setup</ProjectSectionHeading>
              <ProjectSectionText>
                rvu ships on npm and reads the session files Claude Code already writes.
                There is nothing to configure or sync. It only reads files under your
                Claude data directory and never modifies them.
              </ProjectSectionText>
            </ProjectTextRow>
            <CodeBlock className={styles.terminal} variant="terminal" title="rvu">
              {install}
            </CodeBlock>
          </ProjectSectionContent>
        </ProjectSection>
          <ProjectSection>
            <ProjectSectionColumns>
              <ProjectSectionContent>
                <ProjectTextRow width="s">
                  <ProjectSectionHeading>Read the run, then resume</ProjectSectionHeading>
                  <ProjectSectionText>
                    rvu parses each session on demand to show cost, turns, cache hit rate,
                    model split and tool use. Press t for a calendar heatmap and thirty
                    days of trends from the same data. You can inspect a session before
                    reopening it.
                  </ProjectSectionText>
                </ProjectTextRow>
              </ProjectSectionContent>
              <Image
                className={styles.screenshot}
                srcSet={[imageRvuTrends, imageRvuTrendsLarge]}
                placeholder={imageRvuTrendsPlaceholder}
                alt="The rvu trends view: a calendar heatmap of daily activity above bordered panes of cost sparklines, hour-of-day and day-of-week bars, and an activity mix breakdown."
                sizes={`(max-width: ${media.mobile}px) 100vw, 50vw`}
              />
            </ProjectSectionColumns>
          </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>Four ways to reopen a session</ProjectSectionHeading>
              <ProjectSectionText>
                Enter launches the selected session; L cycles what launching means. A live
                session is forked rather than blocked, so picking one up never disturbs
                the run that is already going.
              </ProjectSectionText>
            </ProjectTextRow>
            <div className={styles.table}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>Launcher</TableHeadCell>
                    <TableHeadCell>What Enter does</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {launchers.map(([mode, behaviour]) => (
                    <TableRow key={mode}>
                      <TableCell>{mode}</TableCell>
                      <TableCell>{behaviour}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow center centerMobile noMargin>
              <ProjectSectionHeading>Try it</ProjectSectionHeading>
              <ProjectSectionText>
                rvu is around nine thousand lines of TypeScript and is licensed under
                Apache-2.0. If you use Claude Code, you can run it with one command.
                Issues and ideas are welcome.
              </ProjectSectionText>
              <Button
                secondary
                iconHoverShift
                icon="chevronRight"
                href="https://www.npmjs.com/package/@txmxthy/rvu"
              >
                Get rvu on npm
              </Button>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}
